import { randomUUID } from '@shared/utils';
import { useMemo } from 'react';

import { Box } from '../box';
import { Chip, type ChipProps } from '../chip';
import { EllipsisText } from '../ellipsis-text';
import { Flex, type FlexProps } from '../flex';
import { Label } from '../label';
import { List } from '../list';
import { Tooltip } from '../tooltip';
import { mergeSx } from '../utils';

type ChipSlotProp = Omit<ChipProps, 'label'>;
type LabelSlotProp = Omit<React.ComponentProps<typeof Label>, 'children'>;

type ChipGroupProps = {
  labels: string[];
  limit: number;
  direction?: 'column' | 'row';
  slotProps?: {
    wrapper?: FlexProps;
    elementsWrapper?: FlexProps;
  };
} & (
  | {
      component?: 'chip';
      slotProps?: {
        chip?: ChipSlotProp;
      };
    }
  | {
      component?: 'label';
      slotProps?: {
        label?: LabelSlotProp;
      };
    }
);

function ChipGroup(props: Readonly<ChipGroupProps>) {
  const { labels, limit, direction = 'column', slotProps = {} } = props;
  const { wrapper: wrapperSlotProps, elementsWrapper: elementsWrapperSlotProps } = slotProps;
  const showingLabels = useMemo(() => labels.slice(0, limit), [labels, limit]);
  const leftLabels = useMemo(() => labels.slice(limit), [labels, limit]);

  return (
    <Flex {...wrapperSlotProps} sx={mergeSx({ gap: 2, alignItems: 'end' }, wrapperSlotProps?.sx)}>
      <Flex
        {...elementsWrapperSlotProps}
        sx={mergeSx({ flexDirection: direction, gap: 1 }, elementsWrapperSlotProps?.sx)}
      >
        {showingLabels.map((label) => {
          if (props.component === 'chip') {
            const chipProps = props.slotProps?.chip ?? {};
            return (
              <Chip key={`${label}-${randomUUID()}`} size='small' label={label} {...chipProps} />
            );
          }

          const labelProps = props.component === 'label' ? (props.slotProps?.label ?? {}) : {};
          return (
            <Label key={`${label}-${randomUUID()}`} color='grey' borderType='round' {...labelProps}>
              <EllipsisText variant='body2Semibold'>{label}</EllipsisText>
            </Label>
          );
        })}
      </Flex>
      {leftLabels.length > 0 ? (
        <Tooltip
          title={
            <List sx={{ listStyleType: 'disc', pl: 2 }}>
              {leftLabels.map((label) => (
                <li key={randomUUID()}>{label}</li>
              ))}
            </List>
          }
        >
          <Box>+{leftLabels.length}</Box>
        </Tooltip>
      ) : null}
    </Flex>
  );
}

export { ChipGroup, type ChipGroupProps };
